/**
 * 「从已出好的图直连全渠道套图」的交接载荷验型(2026-07-29 T4)。
 *
 * 与 `parseScenePreset` / `parsePosterExplosiveState` 同口径:**逐字段验型,形状不对
 * 整包丢弃**。陈年 history state 里半份脏数据比没有更危险 —— 它会一路走到出图请求,
 * 而界面上看起来像「用户就是这么选的」。
 */
import type { Ratio } from "./useFreeCreateForm";

export interface RatioSetHandoff {
  taskId: number;
  assetSeq: number;
  /**
   * ★产物 id(2026-07-30,任务 21)。新路只靠它:后端按 assetId 在对象存储内部复制成参考图,
   * **浏览器不再下载 4K 原图** —— 那条路被页面 CSP `connect-src 'self'` 拦死(实测:同一个
   * URL `<img>` 能加载,`fetch` 连 no-cors 都抛),用户看到的就是原生 `Failed to fetch`。
   *
   * 老的 history state 里没有这个字段(用户开着页面时我们发了版),所以是可选:缺了就退回
   * 旧的浏览器搬图路,并在失败时说人话 —— 不因为一次发版把正在操作的人卡死。
   */
  assetId?: number;
  /** ★无 overlay 的 4K 原图 presigned URL。成品图(带 AIGC 角标 + LOGO/二维码)不能当 i2i 底图。 */
  baseUrl: string;
  /** 原任务的出图指令,用于预填(可为空)。 */
  prompt: string;
}

export function parseRatioSetHandoff(state: unknown): RatioSetHandoff | null {
  if (!state || typeof state !== "object") return null;
  const raw = (state as Record<string, unknown>).ratioSetFrom;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.taskId !== "number" || !Number.isFinite(r.taskId)) return null;
  if (typeof r.assetSeq !== "number" || !Number.isFinite(r.assetSeq)) return null;
  if (typeof r.baseUrl !== "string" || !r.baseUrl) return null;
  const prompt = typeof r.prompt === "string" ? r.prompt : "";
  const assetId =
    typeof r.assetId === "number" && Number.isFinite(r.assetId) ? r.assetId : undefined;
  return { taskId: r.taskId, assetSeq: r.assetSeq, assetId, baseUrl: r.baseUrl, prompt };
}

/**
 * 直连套图时预填的指令。
 *
 * ★不留空:空指令 + 挂了原图 = 引擎自由发挥,而用户的意图明确是「这张图,换几个画幅」。
 *   措辞与 ai 侧 `_ratio_reframe_clause` 同口径(那边是逐张追加的画幅指令,这边是整体意图)。
 */
export function ratioSetPrompt(originalPrompt: string): string {
  const head = "保持这张图的主体、构图与文字内容不变,按各渠道画幅重新安排留白与元素位置。";
  return originalPrompt.trim() ? `${head}\n(原图指令:${originalPrompt.trim()})` : head;
}

/** 交接进来的默认画幅集合(渠道预设的默认三个)。 */
export const HANDOFF_DEFAULT_RATIOS: Ratio[] = ["9:16", "3:4", "1:1"];
